import IframeClient from '@/hosted_fields/common/connection/client';
import '@/helpers/polyfills';
import CheckoutUtilsPluginInterface from '@/plugins/checkout_utils/actions/interface';
import {ApiClientInterface} from '@/plugins/core/api/interface';
import {PluginConfig} from '@/plugins/core/interface';
import CheckoutUtilApis from '@/plugins/checkout_utils/api';
import CommManagerInterface from '@/hosted_fields/master/interface';
import {ResponseInnerMessage, Translations} from '@/hosted_fields/common/types';
import {Info, HpData} from '@/plugins/checkout_utils/types';
import {CbError} from '@/hosted_fields/common/errors';
import {updateMerchantDomainValidation} from '@/helpers/domain-validation';
import Logger from '@/utils/logger_old';
import {setLocaleText, getAllTranslations, sanitizeLocale} from '@/hosted_fields/common/locale';

export default class CheckoutUtils implements CheckoutUtilsPluginInterface {
  config: PluginConfig;
  private iframeClient: IframeClient;
  private apiClient: ApiClientInterface;
  private hpData: HpData;
  private commMgr: CommManagerInterface;

  private info: Info;
  private infoRetrievalCount: number;

  // Plugin initialize
  init(): Promise<any> {
    this.apiClient.addApis(CheckoutUtilApis);
    this.hpData = {};
    this.infoRetrievalCount = 0;
    return this.getInfo();
  }

  /**
   * Retrieves the service discovery payload if not loaded already, and initialize the recaptcha
   * script. Initially would attempt to get the response from the CDN url, but for retries would
   * directly request the chargebee API for response, with a backoff clipped at max 5 retries
   * in total.
   */
  public getInfo(): Promise<Info> {
    if (this.info) {
      return Promise.resolve(this.info);
    }
    if (this.infoRetrievalCount >= 5) {
      return Promise.resolve({site: this.config.site});
    }
    return (this.infoRetrievalCount > 1 ? this.apiClient.js_info.get() : this.apiClient.js_info.static())
      .then((info: Info) => {
        this.infoRetrievalCount = -1;
        this.info = info;
        const windowUrl = this.commMgr.site_meta && this.commMgr.site_meta.window_url;
        updateMerchantDomainValidation(info, windowUrl);
        const captchaKey = info.captcha_site_key || info.recaptcha_site_key;
        if (captchaKey) {
          this.commMgr.initializeCaptcha({
            siteKey: captchaKey,
            provider: info.captcha_provider,
            isGlobalRecaptcha: info.supported_global_recaptcha,
            sdkEndpoint: info.captcha_provider_sdk_endpoint || '',
          });
        }
        return info;
      })
      .catch((err) => {
        Logger.kvl(
          {
            action: 'retrieve_js_info_failed',
            info_retrieval_count: this.infoRetrievalCount,
          },
          err
        );
        return Promise.resolve({site: this.config.site});
      })
      .finally(() => this.infoRetrievalCount++);
  }

  /*
    Method to synchronize translations in Master & components (jsdomain)
  */
  private syncTranslations(translations: Translations) {
    // Setting translations in master
    setLocaleText(translations);

    // Sending translations to components (cbjs domain)
    // this.commMgr.getPlugin(PluginName.COMPONENTS_FIELDS)
    //   .then((plugin: ComponentsAndFieldsPluginInterface) => {
    //     plugin.broadcast({
    //       event: Events.setTranslations,
    //       frame: window.name,
    //       data: { translations }
    //     })
    //   })
  }

  getTranslations(data: any = {}): Promise<ResponseInnerMessage> {
    const locale = sanitizeLocale(data.locale);
    const languageCode = data.languageCode;
    const translations = getAllTranslations();

    if (!locale) return Promise.resolve(translations);
    if (translations[locale]) return Promise.resolve(translations[locale]);

    return this.apiClient.locale
      .retrieve({languageCode})
      .then((translations: Translations) => {
        this.syncTranslations(translations);
        return translations; // Sending translations to host domain
      })
      .catch((error) => {
        const err = new CbError(error);
        Logger.error(err);
        throw err;
      });
  }

  getHpData(param?: string): HpData | any {
    if (param && JSON.stringify(this.hpData[param])) return {[param]: this.hpData[param]};
    else if (param) return {};
    return this.hpData;
  }

  portalLogout(): Promise<ResponseInnerMessage> {
    if (!this.hpData.jwt) return Promise.resolve({success: false});
    return this.apiClient.portal.logout({}, {token: this.hpData.jwt}).then(() => {
      this.hpData.jwt = '';
      return {success: true};
    });
  }

  setHpData(data): Promise<ResponseInnerMessage> {
    Object.keys(data).map((key) => {
      if (JSON.stringify(data[key])) {
        this.hpData[key] = data[key];
      }
    });
    return Promise.resolve({acknowledged: true});
  }

  constructor(commMgr: CommManagerInterface) {
    this.config = {
      site: commMgr.site,
      hostname: commMgr.hostName,
      publishableKey: commMgr.publishableKey,
    };
    this.apiClient = commMgr.apiClient;
    this.iframeClient = commMgr.connectionClient;
    this.commMgr = commMgr;
  }

  getBinData(data: any): Promise<ResponseInnerMessage> {
    return this.apiClient.card_bin.get_card_bin_data({}, data);
  }
}
